import { ExternalLink, Globe } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { useLinkPreview } from '@/hooks/useLinkPreview';
import { VideoEmbed } from '@/components/VideoEmbed';

interface LinkPreviewProps {
  url: URL;
  className?: string;
}

export function LinkPreview({ url, className }: LinkPreviewProps) {
  const { data: preview, isLoading, error } = useLinkPreview(url);

  const handleVisitUrl = () => {
    window.open(url.toString(), '_blank', 'noopener,noreferrer');
  };

  if (isLoading) {
    return (
      <Card className={className}>
        <CardContent className="p-6">
          <div className="flex space-x-4">
            <Skeleton className="h-20 w-20 rounded-lg flex-shrink-0" />
            <div className="flex-1 space-y-3">
              <Skeleton className="h-6 w-3/4" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-2/3" />
              <div className="flex items-center space-x-2">
                <Skeleton className="h-4 w-4 rounded-full" />
                <Skeleton className="h-4 w-32" />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error || !preview) {
    return (
      <Card className={className}>
        <CardContent className="p-6">
          <div className="flex items-start justify-between">
            <div className="flex items-center space-x-3">
              <div
                className="h-12 w-12 bg-gray-100 dark:bg-gray-800 rounded-lg flex items-center justify-center cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                onClick={handleVisitUrl}
              >
                <Globe className="h-6 w-6 text-gray-400" />
              </div>
              <div>
                <h3
                  className="font-medium text-gray-900 dark:text-gray-100 cursor-pointer hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                  onClick={handleVisitUrl}
                >
                  {url.hostname}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 font-mono break-all">
                  {url.toString()}
                </p>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleVisitUrl}
              className="flex-shrink-0 self-start"
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              Visit
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // If we have a video embed, show the video component instead
  if (preview.embed) {
    return (
      <VideoEmbed
        embed={preview.embed}
        originalUrl={url.toString()}
        title={preview.title}
        className={className}
      />
    );
  }

  return (
    <Card className={className}>
      <CardContent className="p-6">
        <div className="flex space-x-4">
          {/* Preview Image */}
          {preview.image && (
            <div className="flex-shrink-0">
              <img
                src={preview.image}
                alt={preview.title || 'Link preview'}
                className="h-20 w-20 object-cover rounded-lg border border-gray-200 dark:border-gray-700 cursor-pointer hover:opacity-80 transition-opacity"
                onClick={handleVisitUrl}
                onError={(e) => {
                  // Hide image if it fails to load
                  e.currentTarget.style.display = 'none';
                }}
              />
            </div>
          )}

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between">
              <div className="flex-1 min-w-0">
                {/* Title */}
                {preview.title && (
                  <h3
                    className="font-semibold text-gray-900 dark:text-gray-100 text-lg leading-tight mb-2 cursor-pointer hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                    onClick={handleVisitUrl}
                  >
                    {preview.title}
                  </h3>
                )}

                {/* Description */}
                {preview.description && (
                  <p className="text-gray-600 dark:text-gray-300 text-sm leading-relaxed mb-3 line-clamp-2">
                    {preview.description}
                  </p>
                )}

                {/* Site Info */}
                <div className="flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400">
                  <img
                    src={preview.favicon ?? `https://external-content.duckduckgo.com/ip3/${url.hostname}.ico`}
                    alt=""
                    className="h-4 w-4 rounded-sm"
                  />
                  <span className="font-mono text-xs">
                    {url.hostname}
                  </span>
                </div>
              </div>

              {/* Visit Button */}
              <Button
                variant="outline"
                size="sm"
                onClick={handleVisitUrl}
                className="flex-shrink-0 ml-4 self-start hidden sm:inline-flex"
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                Visit
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}